import React, {
  useEffect,
  useLayoutEffect,
  useRef,
} from "react";
import PropTypes from "prop-types";

import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

import Container from "@components/Container";
import RichText from "@components/RichText";

import { useStore } from "@Store/";
import IconArrowRight from '@svgs/IconArrowRight'

import Mtm from '@svgs/_About/Mtm'
import Bravebison from '@svgs/_About/Bravebison'
import Socialchain from '@svgs/_About/Socialchain'
import Engage from '@svgs/_About/Engage'
import Minimba from '@svgs/_About/Minimba'

import {
  ModelSideBySideMain,
  ModelSideBySideWrapper,
  ModelSideBySideItems,
  ModelSideBySideItem,
  ModelSideBySideItemTimeline,
  ModelSideBySideItemLine,
  ModelSideBySideItemCircle,
  ModelSideBySideItemCircleProgress,
  ModelSideBySideItemContent,
  ModelSideBySideItemTitle,
  ModelSideBySideItemText,
  ModelSideBySideItemSubText,
  LogosContainer
} from "./index.style";
import PageTransitionLink from "@components/PageTransitionLink";

gsap.registerPlugin(ScrollTrigger);

const useIsomorphicLayoutEffect =
  typeof window !== "undefined"
    ? useLayoutEffect
    : useEffect;

const LINE_DURATION = 1.5;
const CIRCLE_DURATION = 0.5;
const CONTENT_DURATION = 1;
const ITEM_DELAY = 0.35;
const CONTENT_STAGGER = 0.18;
const LOGO_DURATION = 0.8;
const LOGO_STAGGER = 0.12;

const TITLE_SLIDE_DISTANCE = 100;
const LOGO_SLIDE_DISTANCE = 50;

const CIRCLE_TOP = 150;
const CIRCLE_SIZE = 40;
const CIRCLE_RADIUS = CIRCLE_SIZE / 2;

const TITLE_TEXT_SELECTOR =
  "p, h1, h2, h3, h4, h5, h6";

const getAnimationElements = (wrapper) => {
  if (!wrapper) return [];

  const childElements = Array.from(
    wrapper.querySelectorAll(
      "p, h1, h2, h3, h4, h5, h6, span, [role='img']",
    ),
  );

  /*
   * data-sub-text may contain plain text without a child
   * paragraph or heading. In that case, animate the wrapper.
   */
  if (!childElements.length) {
    return [wrapper];
  }

  return childElements;
};

const getTitleAnimationElements = (wrapper) => {
  if (!wrapper) return [];

  const textElements = Array.from(
    wrapper.querySelectorAll(TITLE_TEXT_SELECTOR),
  );

  /*
   * data-title may contain plain text without a child
   * paragraph or heading. In that case, animate the wrapper.
   */
  if (!textElements.length) {
    return [wrapper];
  }

  return textElements;
};

const getExternalVisualElements = (
  item,
  titleWrapper,
  subTextWrapper,
  textWrapper,
  logosWrapper,
) => {
  if (!item) return [];

  return Array.from(
    item.querySelectorAll("[role='img']"),
  ).filter(
    (element) =>
      !titleWrapper?.contains(element) &&
      !subTextWrapper?.contains(element) &&
      !textWrapper?.contains(element) &&
      !logosWrapper?.contains(element),
  );
};

const getLogoAnimationElements = (wrapper) => {
  if (!wrapper) return [];

  return Array.from(wrapper.children).filter(Boolean);
};

const getCircleAnimationPosition = (item) => {
  if (!item?.offsetHeight) return 0;

  const lineDistanceToCircle =
    CIRCLE_TOP - CIRCLE_RADIUS;

  const lineProgress = gsap.utils.clamp(
    0,
    1,
    lineDistanceToCircle / item.offsetHeight,
  );

  return LINE_DURATION * lineProgress;
};

const OurModelSideBySide = ({ list, id }) => {
  const [store] = useStore();

  const sectionRef = useRef(null);
  const itemsRef = useRef([]);
  const smoothScrollRef = useRef(
    store?.smoothScroll,
  );

  const sectionId =
    id || "our-model-side-by-side";

  smoothScrollRef.current = store?.smoothScroll;

  const addItemRef = (element, index) => {
    itemsRef.current[index] = element;
  };

  useIsomorphicLayoutEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    const section = sectionRef.current;
    const items = itemsRef.current.filter(Boolean);

    if (!section || !items.length) {
      return undefined;
    }

    let firstFrameId;
    let secondFrameId;
    let resizeFrameId;

    const itemTimelines = [];
    const itemTriggers = [];

    const refresh = () => {
      if (
        smoothScrollRef.current &&
        typeof smoothScrollRef.current.refresh ===
          "function"
      ) {
        smoothScrollRef.current.refresh();
      } else {
        ScrollTrigger.refresh();
      }
    };

    const refreshAfterLayout = () => {
      if (resizeFrameId) {
        cancelAnimationFrame(resizeFrameId);
      }

      resizeFrameId = requestAnimationFrame(() => {
        refresh();
      });
    };

    const playTimelineWhenReady = (
      timeline,
      trigger,
    ) => {
      if (!timeline || !trigger) return;

      const currentScroll = trigger.scroll();

      if (
        trigger.isActive ||
        currentScroll >= trigger.start
      ) {
        timeline.play();
      }
    };

    const ctx = gsap.context(() => {
      const mm = gsap.matchMedia();

      mm.add("(min-width:1024px)", () => {
        firstFrameId = requestAnimationFrame(() => {
          secondFrameId = requestAnimationFrame(() => {
            items.forEach((item, index) => {
              const isLeft = index % 2 !== 0;

              const line = item.querySelector(
                "[data-timeline-line]",
              );

              const circleProgress =
                item.querySelector(
                  "[data-circle-progress]",
                );

              const titleWrapper =
                item.querySelector("[data-title]");

              const subTextWrapper =
                item.querySelector(
                  "[data-sub-text]",
                );

              const textWrapper =
                item.querySelector("[data-text]");

              const logosWrapper =
                item.querySelector(
                  "[data-logos-container]",
                );

              if (!line || !circleProgress) {
                return;
              }

              const titleSlideElements =
                getTitleAnimationElements(
                  titleWrapper,
                );

              const titleVisualElements =
                titleWrapper
                  ? Array.from(
                      titleWrapper.querySelectorAll(
                        "[role='img']",
                      ),
                    )
                  : [];

              const subTextElements =
                getAnimationElements(
                  subTextWrapper,
                );

              const textElements =
                getAnimationElements(textWrapper);

              const logoElements =
                getLogoAnimationElements(
                  logosWrapper,
                );

              const externalVisualElements =
                getExternalVisualElements(
                  item,
                  titleWrapper,
                  subTextWrapper,
                  textWrapper,
                  logosWrapper,
                );

              /*
               * Elements that keep the original flip/scale
               * entrance treatment (title visuals + any
               * external inline visuals not covered by
               * data-sub-text / data-text).
               */
              const flipElements = Array.from(
                new Set([
                  ...titleVisualElements,
                  ...externalVisualElements,
                ]),
              ).filter(Boolean);

              /*
               * data-sub-text and data-text share the same
               * simple slide-up entrance animation. data-sub-text
               * is included alongside data-text so both receive
               * identical treatment.
               */
              const slideUpElements = Array.from(
                new Set([
                  ...subTextElements,
                  ...textElements,
                ]),
              ).filter(Boolean);

              const allEntranceElements = Array.from(
                new Set([
                  ...flipElements,
                  ...slideUpElements,
                ]),
              );

              const inlineVisualElements =
                allEntranceElements.filter(
                  (element) =>
                    element.matches("[role='img']"),
                );

              gsap.set(line, {
                scaleY: 0,
                transformOrigin: "top center",
              });

              gsap.set(circleProgress, {
                "--circle-progress": 0,
              });

              if (inlineVisualElements.length) {
                gsap.set(inlineVisualElements, {
                  display: "inline-block",
                });
              }

              /*
               * Right-side items slide their title in from the
               * right (moving left into place); left-side items
               * slide their title in from the left (moving right
               * into place).
               */
              const titleStartX = isLeft
                ? -TITLE_SLIDE_DISTANCE
                : TITLE_SLIDE_DISTANCE;

              if (titleSlideElements.length) {
                gsap.set(titleSlideElements, {
                  autoAlpha: 0,
                  x: titleStartX,
                  willChange:
                    "transform,opacity",
                });
              }

              if (flipElements.length) {
                gsap.set(flipElements, {
                  autoAlpha: 0,
                  scale: 0,
                  y: 80,
                  rotationX: 180,
                  transformOrigin:
                    "50% 0% -50px",
                  transformPerspective: 1000,
                  willChange:
                    "transform,opacity",
                });
              }

              if (slideUpElements.length) {
                gsap.set(slideUpElements, {
                  autoAlpha: 0,
                  y: 40,
                  willChange:
                    "transform,opacity",
                });
              }

              if (logoElements.length) {
                gsap.set(logoElements, {
                  autoAlpha: 0,
                  y: LOGO_SLIDE_DISTANCE,
                  willChange:
                    "transform,opacity",
                });
              }

              const circleAnimationPosition =
                getCircleAnimationPosition(item);

              const itemTimeline = gsap.timeline({
                paused: true,
                delay:
                  index === 0 ? 0 : ITEM_DELAY,
              });

              itemTimeline.to(line, {
                scaleY: 1,
                duration: LINE_DURATION,
                ease: "power2.inOut",
              });

              itemTimeline.to(
                circleProgress,
                {
                  "--circle-progress": 1,
                  duration: CIRCLE_DURATION,
                  ease: "none",
                },
                circleAnimationPosition,
              );

              if (titleSlideElements.length) {
                itemTimeline.to(
                  titleSlideElements,
                  {
                    autoAlpha: 1,
                    x: 0,
                    duration: CONTENT_DURATION,
                    stagger: CONTENT_STAGGER,
                    ease: "power3.out",
                    clearProps:
                      "transform,opacity,visibility,willChange",
                  },
                  circleAnimationPosition,
                );
              }

              if (flipElements.length) {
                itemTimeline.to(
                  flipElements,
                  {
                    autoAlpha: 1,
                    scale: 1,
                    y: 0,
                    rotationX: 0,
                    duration: CONTENT_DURATION,
                    stagger: CONTENT_STAGGER,
                    ease: "back.out(1.7)",
                    clearProps:
                      "transform,opacity,visibility,transformOrigin,transformPerspective,willChange",
                  },
                  circleAnimationPosition + 0.15,
                );
              }

              if (slideUpElements.length) {
                itemTimeline.to(
                  slideUpElements,
                  {
                    autoAlpha: 1,
                    y: 0,
                    duration: CONTENT_DURATION,
                    stagger: CONTENT_STAGGER,
                    ease: "power3.out",
                    clearProps:
                      "transform,opacity,visibility,willChange",
                  },
                  circleAnimationPosition + 0.15,
                );
              }

              if (logoElements.length) {
                itemTimeline.to(
                  logoElements,
                  {
                    autoAlpha: 1,
                    y: 0,
                    duration: LOGO_DURATION,
                    stagger: LOGO_STAGGER,
                    ease: "power3.out",
                    clearProps:
                      "transform,opacity,visibility,willChange",
                  },
                  circleAnimationPosition + 0.3,
                );
              }

              const itemTrigger =
                ScrollTrigger.create({
                  id: `${sectionId}-item-${index}`,
                  trigger: item,
                  start: "top 90%",
                  end: "bottom top",
                  once: true,
                  invalidateOnRefresh: true,
                  onEnter: () => {
                    itemTimeline.play();
                  },
                  onRefresh: (self) => {
                    playTimelineWhenReady(
                      itemTimeline,
                      self,
                    );
                  },
                });

              itemTimelines.push(itemTimeline);
              itemTriggers.push(itemTrigger);
            });

            refresh();

            itemTriggers.forEach(
              (itemTrigger, index) => {
                playTimelineWhenReady(
                  itemTimelines[index],
                  itemTrigger,
                );
              },
            );
          });
        });

        return () => {
          itemTriggers.forEach((trigger) => {
            trigger.kill();
          });

          itemTimelines.forEach((timeline) => {
            timeline.kill();
          });
        };
      });

      mm.add("(max-width:1023px)", () => {
        const animatedElements = items.flatMap(
          (item) => [
            item.querySelector(
              "[data-timeline-line]",
            ),
            item.querySelector(
              "[data-circle-progress]",
            ),
            ...getTitleAnimationElements(
              item.querySelector("[data-title]"),
            ),
            ...getAnimationElements(
              item.querySelector("[data-title]"),
            ),
            ...getAnimationElements(
              item.querySelector(
                "[data-sub-text]",
              ),
            ),
            ...getAnimationElements(
              item.querySelector("[data-text]"),
            ),
            ...getLogoAnimationElements(
              item.querySelector(
                "[data-logos-container]",
              ),
            ),
          ],
        );

        gsap.set(
          animatedElements.filter(Boolean),
          {
            clearProps: "all",
          },
        );
      });

      return () => {
        mm.revert();
      };
    }, section);

    window.addEventListener(
      "load",
      refreshAfterLayout,
    );

    window.addEventListener(
      "resize",
      refreshAfterLayout,
    );

    return () => {
      if (firstFrameId) {
        cancelAnimationFrame(firstFrameId);
      }

      if (secondFrameId) {
        cancelAnimationFrame(secondFrameId);
      }

      if (resizeFrameId) {
        cancelAnimationFrame(resizeFrameId);
      }

      ctx.revert();

      window.removeEventListener(
        "load",
        refreshAfterLayout,
      );

      window.removeEventListener(
        "resize",
        refreshAfterLayout,
      );

      itemsRef.current = [];
    };
  }, [
    list.length,
    sectionId,
    store?.smoothScroll,
  ]);

  return (
    <ModelSideBySideMain
      id={sectionId}
      ref={sectionRef}
    >
      <Container>
        <ModelSideBySideWrapper>
          <ModelSideBySideItems>
            {list.map((item, index) => {
              const isLeft = index % 2 !== 0;

              return (
                <ModelSideBySideItem
                  key={
                    item?.id ||
                    item?.contentful_id ||
                    index
                  }
                  ref={(element) =>
                    addItemRef(element, index)
                  }
                  $isLeft={isLeft}
                >
                  <ModelSideBySideItemTimeline
                    aria-hidden="true"
                  >
                    <ModelSideBySideItemLine
                      data-timeline-line
                    />

                    <ModelSideBySideItemCircle>
                      <ModelSideBySideItemCircleProgress
                        data-circle-progress
                      />
                    </ModelSideBySideItemCircle>
                  </ModelSideBySideItemTimeline>

                  <ModelSideBySideItemContent
                    $isLeft={isLeft}
                  >
                    {item?.subText && (
                      <ModelSideBySideItemTitle
                        data-title
                      >
                        <RichText
                          content={item.subText}
                        />
                      </ModelSideBySideItemTitle>
                    )}

                    {item?.text && (
                      <ModelSideBySideItemText
                        data-text
                        $isLeft={isLeft}
                      >
                        <RichText
                          content={item.text}
                        />
                      </ModelSideBySideItemText>
                    )}
                    <LogosContainer
                      data-logos-container
                      $isLeft={isLeft}
                    >
                      {item?.url === 'mtm' &&
                        <a href="https://wearemtm.com/" target="_blank">
                          <Mtm className="logo" />
                        </a>
                      }
                      {item?.url === 'minimba' &&
                        <a href="https://minimba.com/" target="_blank">
                          <Minimba className="logo"/>
                        </a>
                      }
                      {item?.url === 'bravebison' &&
                        <>
                        <a href="https://www.engagedigitalpartners.com/" target="_blank">
                          <Engage className="logo engage-logo"/>
                        </a>
                        <a href="https://www.socialchain.com/" target="_blank">
                          <Socialchain className="logo"/>
                        </a>
                        <PageTransitionLink to="what-we-do/paid-performance">
                          <Bravebison className="logo"/>
                        </PageTransitionLink>
                        </>
                      }
                      <IconArrowRight className="arrow-icon"/>
                    </LogosContainer>
                  </ModelSideBySideItemContent>
                </ModelSideBySideItem>
              );
            })}
          </ModelSideBySideItems>
        </ModelSideBySideWrapper>
      </Container>
    </ModelSideBySideMain>
  );
};

OurModelSideBySide.propTypes = {
  id: PropTypes.string,
  list: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      contentful_id: PropTypes.string,
      text: PropTypes.object,
      subText: PropTypes.object,
      textOld: PropTypes.shape({
        textOld: PropTypes.string,
      }),
    }),
  ),
};

OurModelSideBySide.defaultProps = {
  id: "our-model-side-by-side",
  list: [],
};

export default OurModelSideBySide;